// How a moment is told, chosen by its author: a memory (then) or something happening as they write (now), and drawn
// as a photo, a few words set large, or a story. The same rules on the server and in the app.

export const TENSES = ['then', 'now'] as const
export type Tense = (typeof TENSES)[number]
export const SHAPES = ['photo', 'words', 'story'] as const
export type Shape = (typeof SHAPES)[number]

// How long a Now moment reads as happening now, with its live dot. After that it's "written at the time".
export const NOW_DAYS = 4
const DAY = 24 * 60 * 60 * 1000

// A moment saved before these choices existed is a memory.
export const tenseOf = (m: { tense: Tense | null }): Tense => m.tense ?? 'then'

// The shape a moment is drawn in. A photo needs a photo to lead with, so one chosen as a photo that has none is drawn
// as its story. A moment that never chose leads with its photo when the story is short, and with the story otherwise.
export function shapeOf(m: { shape: Shape | null; photoCount: number; body: string }): Shape {
  if (m.shape === 'photo') return m.photoCount > 0 ? 'photo' : 'story'
  if (m.shape) return m.shape
  return m.photoCount > 0 && m.body.length <= 280 ? 'photo' : 'story'
}

// Whether a Now moment still reads as happening: written in the last few days.
export const isLive = (m: { tense: Tense | null; createdAt: number }, now = Date.now()) =>
  m.tense === 'now' && now - m.createdAt < NOW_DAYS * DAY

// The decade a date falls in, as its first year ("1990" for 1994), or null when there's no date.
export const decadeOf = (happenedOn: string | null) =>
  happenedOn ? String(Math.floor(Number(happenedOn.slice(0, 4)) / 10) * 10) : null

// When a Now moment happened, as a person says it while it's recent: "This morning", "Last night", "Yesterday",
// "Tuesday". Only for the days it's live; older ones show their date.
export function nowSaid(createdAt: number, now = new Date()) {
  const then = new Date(createdAt)
  const days = Math.round((startOfDay(now) - startOfDay(then)) / DAY)
  const hour = then.getHours()
  if (days === 0) return hour < 12 ? 'This morning' : hour < 17 ? 'This afternoon' : 'Tonight'
  if (days === 1) return hour >= 18 ? 'Last night' : 'Yesterday'
  return then.toLocaleDateString('en-GB', { weekday: 'long' })
}
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

// Today's date in the writer's own day, as a moment keeps it (YYYY-MM-DD).
export function today(now = new Date()) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}
